<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="mb-4 flex flex-wrap gap-3 items-end justify-between">
      <div class="flex flex-wrap gap-3 items-end">
        <div>
          <label class="text-xs text-gray-500">{{ $t('smm.board.project') }}</label>
          <select v-model="projectId" class="block border rounded-lg p-2 mt-1 text-sm min-w-[180px]" @change="loadBoard">
            <option value="">{{ $t('common.all') }}</option>
            <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
      </div>
      <button
        type="button"
        class="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        :disabled="!projects.length"
        @click="openCreate"
      >
        {{ $t('smm.board.create_ticket') }}
      </button>
    </div>

    <p v-if="error" class="text-sm text-red-600 mb-3 whitespace-pre-line">{{ error }}</p>
    <div v-if="loading" class="text-center text-gray-500 py-12">{{ $t('common.loading') }}</div>

    <div v-else-if="!projects.length" class="text-center text-gray-500 py-12">
      {{ $t('smm.board.no_projects') }}
      <router-link :to="{ name: 'SmmSettingsBoard' }" class="text-blue-600 hover:underline ml-1">
        {{ $t('smm.board.go_settings') }}
      </router-link>
    </div>

    <div v-else class="flex gap-3 overflow-x-auto pb-4 items-start">
      <div
        v-for="col in columns"
        :key="col.status.id"
        class="min-w-[260px] w-[280px] shrink-0 bg-gray-100 rounded-lg p-2"
      >
        <div class="flex items-center gap-2 px-2 py-2">
          <span
            class="w-2.5 h-2.5 rounded-full shrink-0"
            :style="{ backgroundColor: col.status.color || '#9CA3AF' }"
          />
          <h3 class="text-sm font-semibold text-gray-700">{{ col.status.name }}</h3>
          <span class="text-xs text-gray-400 ml-auto">{{ (col.tickets || []).length }}</span>
        </div>
        <div
          class="space-y-2 min-h-[140px] rounded-md p-1 transition-colors"
          :class="dragOverStatusId === col.status.id ? 'bg-blue-50 ring-1 ring-blue-200' : ''"
          @dragover.prevent="onColumnDragOver($event, col)"
          @drop.prevent="onColumnDrop($event, col)"
          @dragenter.prevent="dragOverStatusId = col.status.id"
          @dragleave="onColumnDragLeave($event, col)"
        >
          <div
            v-for="(card, index) in col.tickets"
            :key="card.id"
            draggable="true"
            class="bg-white rounded-lg p-3 shadow-sm border text-sm cursor-grab active:cursor-grabbing hover:border-blue-300 select-none"
            :class="[
              priorityBorderClass(card.priority),
              dragTicketId === card.id ? 'opacity-40' : '',
              dropIndicator.statusId === col.status.id && dropIndicator.index === index
                ? 'outline outline-2 outline-offset-2 outline-blue-400'
                : '',
            ]"
            @dragstart="onCardDragStart($event, card, col, index)"
            @dragend="onCardDragEnd"
            @dragover.prevent="onCardDragOver($event, col, index)"
            @drop.prevent="onColumnDrop($event, col)"
            @click="openTicket(card)"
          >
            <div class="flex items-center gap-2 mb-1">
              <span
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium"
                :class="priorityBadgeClass(card.priority)"
              >{{ $t(`smm.board.priority_${card.priority || 'medium'}`) }}</span>
              <span
                v-if="showProjectOnCards && card.project"
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white border text-gray-600 truncate max-w-[7rem]"
                :title="card.project.name"
              >{{ card.project.name }}</span>
              <span
                v-if="card.type"
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                :style="{
                  backgroundColor: (card.type.color || '#94a3b8') + '22',
                  color: card.type.color || '#64748b',
                }"
              >{{ card.type.name }}</span>
            </div>
            <p class="font-medium text-gray-900 leading-snug">{{ card.title }}</p>
            <p v-if="card.due_date" class="text-xs text-gray-500 mt-1.5">
              {{ $t('smm.board.due_short') }}: {{ formatDate(card.due_date) }}
            </p>
            <div v-if="cardImages(card).length" class="flex gap-1 mt-2">
              <img
                v-for="file in cardImages(card).slice(0, 3)"
                :key="file.id"
                :src="file.url"
                class="w-8 h-8 rounded object-cover border border-gray-100 pointer-events-none"
                alt=""
                draggable="false"
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create ticket dialog -->
    <div
      v-if="showCreate"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="showCreate = false"
    >
      <div class="bg-white rounded-lg w-full max-w-md p-5 shadow-xl space-y-3">
        <h3 class="font-semibold text-gray-900">{{ $t('smm.board.create_ticket') }}</h3>
        <div v-if="!projectId">
          <label class="text-xs text-gray-500">{{ $t('smm.board.project') }}</label>
          <select v-model="createForm.project_id" class="w-full border rounded-lg p-2 mt-1 text-sm">
            <option :value="null" disabled>{{ $t('common.select_placeholder') }}</option>
            <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <div>
          <label class="text-xs text-gray-500">{{ $t('smm.board.ticket_title') }}</label>
          <input v-model="createForm.title" class="w-full border rounded-lg p-2 mt-1 text-sm" />
        </div>
        <div>
          <label class="text-xs text-gray-500">{{ $t('smm.board.type') }}</label>
          <select v-model="createForm.type_id" class="w-full border rounded-lg p-2 mt-1 text-sm">
            <option :value="null">—</option>
            <option v-for="t in types" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </div>
        <div>
          <label class="text-xs text-gray-500">{{ $t('smm.board.priority') }}</label>
          <select v-model="createForm.priority" class="w-full border rounded-lg p-2 mt-1 text-sm">
            <option value="low">{{ $t('smm.board.priority_low') }}</option>
            <option value="medium">{{ $t('smm.board.priority_medium') }}</option>
            <option value="high">{{ $t('smm.board.priority_high') }}</option>
            <option value="urgent">{{ $t('smm.board.priority_urgent') }}</option>
          </select>
        </div>
        <p v-if="createError" class="text-sm text-red-600">{{ createError }}</p>
        <div class="flex justify-end gap-2 pt-2">
          <button type="button" class="px-3 py-2 text-sm rounded-lg border" @click="showCreate = false">
            {{ $t('common.cancel') }}
          </button>
          <button
            type="button"
            class="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            :disabled="creating || !createForm.title.trim() || !createProjectId"
            @click="createTicket"
          >
            {{ creating ? $t('common.saving') : $t('common.add') }}
          </button>
        </div>
      </div>
    </div>

    <board-ticket-modal
      :show="!!openTicketId"
      :ticket-id="openTicketId"
      :statuses="statuses"
      :types="types"
      :projects="projects"
      @close="openTicketId = null"
      @updated="onTicketUpdated"
      @deleted="loadBoard"
      @open-ticket="openTicketById"
    />
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'
import BoardTicketModal from '@/components/smm/BoardTicketModal.vue'

export default {
  components: { SmmSubNav, BoardTicketModal },
  data: () => ({
    projects: [],
    projectId: '',
    columns: [],
    types: [],
    statuses: [],
    loading: true,
    error: null,
    showCreate: false,
    creating: false,
    createError: null,
    createForm: {
      project_id: null,
      title: '',
      type_id: null,
      priority: 'medium',
    },
    openTicketId: null,
    suppressClick: false,
    dragTicketId: null,
    dragFromStatusId: null,
    dragFromIndex: null,
    dragOverStatusId: null,
    dropIndicator: { statusId: null, index: null },
    savingOrder: false,
  }),
  computed: {
    showProjectOnCards() {
      return !this.projectId
    },
    createProjectId() {
      return this.projectId || this.createForm.project_id
    },
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    formatValidationError(e) {
      const data = e.response?.data
      if (!data) return null
      if (data.message) return data.message
      if (data.errors) return Object.values(data.errors).flat().join('\n')
      return null
    },
    formatDate(value) {
      if (!value) return ''
      try {
        return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString(
          this.$i18n.locale === 'ru' ? 'ru-RU' : 'en-US',
          { day: 'numeric', month: 'short' }
        )
      } catch (_) {
        return String(value).slice(0, 10)
      }
    },
    cardImages(card) {
      return (card.files || []).filter((f) => f.type === 'image')
    },
    priorityBadgeClass(priority) {
      switch (priority) {
        case 'urgent':
          return 'bg-red-100 text-red-700'
        case 'high':
          return 'bg-orange-100 text-orange-700'
        case 'low':
          return 'bg-slate-100 text-slate-500'
        default:
          return 'bg-blue-50 text-blue-700'
      }
    },
    priorityBorderClass(priority) {
      switch (priority) {
        case 'urgent':
          return 'border-l-4 border-l-red-500'
        case 'high':
          return 'border-l-4 border-l-orange-400'
        case 'low':
          return 'border-l-4 border-l-slate-300'
        default:
          return 'border-l-4 border-l-blue-400'
      }
    },
    onCardDragStart(event, card, col, index) {
      this.suppressClick = true
      this.dragTicketId = card.id
      this.dragFromStatusId = col.status.id
      this.dragFromIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(card.id))
      try {
        event.dataTransfer.setData('application/x-ticket-id', String(card.id))
      } catch (_) {
        /* IE / some browsers */
      }
    },
    onCardDragEnd() {
      this.dragTicketId = null
      this.dragFromStatusId = null
      this.dragFromIndex = null
      this.dragOverStatusId = null
      this.dropIndicator = { statusId: null, index: null }
      setTimeout(() => {
        this.suppressClick = false
      }, 100)
    },
    onColumnDragOver(event, col) {
      event.dataTransfer.dropEffect = 'move'
      this.dragOverStatusId = col.status.id
      if (this.dropIndicator.statusId !== col.status.id || this.dropIndicator.index == null) {
        this.dropIndicator = {
          statusId: col.status.id,
          index: (col.tickets || []).length,
        }
      }
    },
    onColumnDragLeave(event, col) {
      const related = event.relatedTarget
      if (related && event.currentTarget.contains(related)) return
      if (this.dragOverStatusId === col.status.id) this.dragOverStatusId = null
    },
    onCardDragOver(event, col, index) {
      event.dataTransfer.dropEffect = 'move'
      this.dragOverStatusId = col.status.id
      const rect = event.currentTarget.getBoundingClientRect()
      const before = event.clientY < rect.top + rect.height / 2
      this.dropIndicator = {
        statusId: col.status.id,
        index: before ? index : index + 1,
      }
    },
    async onColumnDrop(event, col) {
      const ticketId = this.dragTicketId
        || Number(event.dataTransfer.getData('application/x-ticket-id') || event.dataTransfer.getData('text/plain'))
      if (!ticketId) return

      let targetIndex = this.dropIndicator.statusId === col.status.id
        ? this.dropIndicator.index
        : (col.tickets || []).length
      if (targetIndex == null) targetIndex = (col.tickets || []).length

      await this.moveTicket(ticketId, col.status.id, targetIndex)
      this.onCardDragEnd()
    },
    findTicketLocation(ticketId) {
      for (const col of this.columns) {
        const index = (col.tickets || []).findIndex((t) => t.id === ticketId)
        if (index !== -1) {
          return { col, index, ticket: col.tickets[index] }
        }
      }
      return null
    },
    async moveTicket(ticketId, toStatusId, toIndex) {
      const location = this.findTicketLocation(ticketId)
      if (!location) return

      const fromStatusId = location.col.status.id
      let insertAt = Math.max(0, Math.min(toIndex, (this.columns.find((c) => c.status.id === toStatusId)?.tickets || []).length))

      // Adjust index when moving down within the same column
      if (fromStatusId === toStatusId && location.index < insertAt) {
        insertAt -= 1
      }
      if (fromStatusId === toStatusId && location.index === insertAt) return

      const ticket = location.ticket
      location.col.tickets.splice(location.index, 1)

      const toCol = this.columns.find((c) => c.status.id === toStatusId)
      if (!toCol) return
      ticket.status_id = toStatusId
      toCol.tickets.splice(insertAt, 0, ticket)

      const tasks = [
        this.persistColumnOrder(toStatusId, toCol.tickets.map((t) => t.id)),
      ]
      if (fromStatusId !== toStatusId) {
        tasks.push(this.persistColumnOrder(fromStatusId, location.col.tickets.map((t) => t.id)))
      }

      this.savingOrder = true
      this.error = null
      try {
        await Promise.all(tasks)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
        await this.loadBoard()
      } finally {
        this.savingOrder = false
      }
    },
    async persistColumnOrder(statusId, ticketIds) {
      await axios.post('/api/v1/smm/board/tickets/reorder', {
        status_id: statusId,
        ticket_ids: ticketIds,
      })
    },
    openCreate() {
      this.createError = null
      this.createForm = {
        project_id: this.projectId || this.projects[0]?.id || null,
        title: '',
        type_id: null,
        priority: 'medium',
      }
      this.showCreate = true
    },
    async bootstrap() {
      this.loading = true
      this.error = null
      try {
        const [projectsRes, statusesRes] = await Promise.all([
          axios.get('/api/v1/smm/board/projects'),
          axios.get('/api/v1/smm/board/statuses'),
        ])
        this.projects = projectsRes.data.data || []
        this.statuses = statusesRes.data.data || []
        this.projectId = ''
        await this.loadBoard()
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
        this.loading = false
      }
    },
    async loadBoard() {
      this.loading = true
      this.error = null
      try {
        const params = {}
        if (this.projectId) params.project_id = this.projectId
        const { data } = await axios.get('/api/v1/smm/board/tickets/board', { params })
        this.columns = data.data.columns || []
        this.types = data.data.types || []
        this.statuses = this.columns.map((c) => c.status)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.loading = false
      }
    },
    openTicket(card) {
      if (this.suppressClick) return
      this.openTicketId = card.id
    },
    openTicketById(id) {
      this.openTicketId = id
    },
    onTicketUpdated() {
      this.loadBoard()
    },
    async createTicket() {
      const projectId = this.createProjectId
      if (!projectId || !this.createForm.title.trim()) return
      this.creating = true
      this.createError = null
      try {
        const { data } = await axios.post('/api/v1/smm/board/tickets', {
          project_id: projectId,
          title: this.createForm.title.trim(),
          type_id: this.createForm.type_id,
          priority: this.createForm.priority,
        })
        this.showCreate = false
        this.createForm = { project_id: null, title: '', type_id: null, priority: 'medium' }
        await this.loadBoard()
        this.openTicketId = data.data.id
      } catch (e) {
        this.createError = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.creating = false
      }
    },
  },
}
</script>
